import { useState, useEffect } from "react";
import {
  UserPlus,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader2,
  AlertCircle,
  Send,
  Copy,
} from "lucide-react";

const Invitaciones = () => {
  const [invitaciones, setInvitaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [adminEmpresa, setAdminEmpresa] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    roles: [], // ✅ Array de roles
  });
  const [roles, setRoles] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadInvitaciones();
    loadRoles();
  }, []);

  const getApiUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      return "http://localhost:8000/api";
    }
    return apiUrl.endsWith("/api") ? apiUrl : `${apiUrl}/api`;
  };

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const loadInvitaciones = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/users/invitations/list`, {
        headers: getHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setInvitaciones(data.data || []);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/users/form/data`, {
        headers: getHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setRoles(data.data?.roles || []);

        // ✅ GUARDAR INFO DE LA EMPRESA
        if (data.data?.admin_empresa) {
          setAdminEmpresa(data.data.admin_empresa);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSubmit = async () => {
    setErrors({});

    // ⭐ VALIDAR QUE HAYA AL MENOS UN ROL SELECCIONADO
    if (!formData.roles || formData.roles.length === 0) {
      setErrors({ roles: ["Debe seleccionar al menos un rol"] });
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/users/invite`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        alert("✅ Invitación enviada exitosamente");
        setShowModal(false);
        setFormData({ email: "", name: "", roles: [] }); // ✅ CORRECTO
        setErrors({});
        loadInvitaciones();
      } else {
        if (data.errors) {
          setErrors(data.errors);
        }
        alert("❌ " + (data.message || "Error al enviar invitación"));
      }
    } catch (error) {
      console.error("Error:", error);
      alert("❌ Error al enviar invitación");
    }
  };

  const handleResend = async (invitationId) => {
    if (!window.confirm("¿Reenviar invitación?")) return;

    try {
      const response = await fetch(
        `${getApiUrl()}/users/invitations/${invitationId}/resend`,
        {
          method: "POST",
          headers: getHeaders(),
        }
      );

      if (response.ok) {
        alert("✅ Invitación reenviada");
        loadInvitaciones();
      } else {
        const data = await response.json();
        alert("❌ " + (data.message || "Error al reenviar"));
      }
    } catch (error) {
      console.error("Error:", error);
      alert("❌ Error al reenviar");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: {
        color: "bg-yellow-100 text-yellow-800",
        icon: Clock,
        text: "Pendiente",
      },
      accepted: {
        color: "bg-green-100 text-green-800",
        icon: CheckCircle,
        text: "Aceptada",
      },
      expired: {
        color: "bg-red-100 text-red-800",
        icon: XCircle,
        text: "Expirada",
      },
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}
      >
        <Icon className="h-3 w-3 mr-1" />
        {badge.text}
      </span>
    );
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/invitation/${token}`; // ⭐ CAMBIAR accept-invitation por invitation
    navigator.clipboard.writeText(link);
    alert("🔗 Link copiado al portapapeles");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Cargando...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <UserPlus className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Invitaciones</h2>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              {invitaciones.length}
            </span>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={loadInvitaciones}
              className="flex items-center px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Actualizar
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Mail className="h-4 w-4 mr-1" />
              Nueva Invitación
            </button>
          </div>
          {adminEmpresa && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
              <p className="text-sm font-semibold text-blue-800 mb-2">
                📧 Invitación a {adminEmpresa.nombre}
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-blue-700 font-medium">Empresa:</span>
                  <span className="ml-2 text-blue-900">
                    {adminEmpresa.nombre}
                  </span>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">
                    Tipo asignado:
                  </span>
                  <span className="ml-2 bg-blue-200 px-2 py-1 rounded text-blue-900 font-medium">
                    {adminEmpresa.tipo_usuario_auto}
                  </span>
                </div>
              </div>
              <p className="text-xs text-blue-700 mt-2">
                ℹ️ El tipo de usuario se asignará automáticamente según su
                empresa
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Usuario
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Roles
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Enviada
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {invitaciones.map((inv) => (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{inv.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {inv.email}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {/* ⭐ MOSTRAR MÚLTIPLES ROLES */}
                    <div className="flex flex-wrap gap-1">
                      {inv.roles_names?.map((roleName, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded"
                        >
                          {roleName}
                        </span>
                      )) || <span className="text-gray-400">Sin roles</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {getStatusBadge(inv.status)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(inv.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <div className="flex items-center justify-end space-x-2">
                      {inv.status === "pending" && (
                        <>
                          <button
                            onClick={() => copyLink(inv.token)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Copiar link"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleResend(inv.id)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Reenviar"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {invitaciones.length === 0 && (
          <div className="text-center py-12">
            <Mail className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No hay invitaciones</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="border-b p-4 sticky top-0 bg-white">
              <h3 className="text-lg font-semibold flex items-center">
                <Mail className="h-5 w-5 mr-2 text-blue-600" />
                Nueva Invitación
              </h3>
            </div>
            <div className="p-4 space-y-4">
              {/* Nombre */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Juan Pérez"
                />
                {errors.name && (
                  <p className="text-xs text-red-500 mt-1 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {errors.name[0]}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="usuario@ejemplo.com"
                />
                {errors.email && (
                  <p className="text-xs text-red-500 mt-1 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {errors.email[0]}
                  </p>
                )}
              </div>

              {/* Roles */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Roles <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 ml-2">
                    ({formData.roles.length} seleccionado
                    {formData.roles.length !== 1 ? "s" : ""})
                  </span>
                </label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {roles.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No hay roles disponibles
                    </p>
                  ) : (
                    roles.map((role) => (
                      <label
                        key={role.id}
                        className="flex items-start cursor-pointer hover:bg-gray-50 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={formData.roles?.includes(role.id) || false}
                          onChange={(e) => {
                            const roles = formData.roles || [];
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                roles: [...roles, role.id],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                roles: roles.filter((id) => id !== role.id),
                              });
                            }
                          }}
                          className="mt-1 mr-2"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{role.name}</div>
                          {role.description && (
                            <div className="text-xs text-gray-500">
                              {role.description}
                            </div>
                          )}
                        </div>
                      </label>
                    ))
                  )}
                </div>
                {errors.roles && (
                  <p className="text-xs text-red-500 mt-1 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {errors.roles[0]}
                  </p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex space-x-2 pt-4">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setFormData({ email: "", name: "", roles: [] });
                    setErrors({});
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={formData.roles.length === 0}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Enviar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invitaciones;
